import { format, formatDistanceToNow, isToday, isTomorrow, differenceInYears } from "date-fns";

// Every date-ONLY field this app has (date of birth, date of joining,
// marriage date, leave dates, holiday dates, ...) is stored as a UTC-
// midnight timestamp with no meaningful time-of-day -- it's a calendar
// date, not an instant. date-fns's own format() reads a Date object
// through the BROWSER'S LOCAL getters, though, not UTC ones: for anyone
// whose local timezone sits behind UTC at all, a stored "2008-08-04
// 00:00 UTC" falls on "2008-08-03" evening locally, and formatDate would
// silently show one day earlier than what's actually stored -- and one
// day earlier than EmployeeFormPage.jsx's own iso() helper, which reads
// the same value's UTC calendar date correctly for the edit form's date
// input. Re-based here onto local midnight for the SAME UTC calendar
// date before formatting, so format()'s local getters can no longer
// shift it a day in either direction depending on the viewer's offset
// from UTC -- this is what keeps the display and the edit form agreeing
// on the same date for every viewer, not just one in Asia/Kolkata.
function toCalendarDate(d) {
  const date = new Date(d);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export const formatDate = (d, pattern = "d MMM yyyy") => (d ? format(toCalendarDate(d), pattern) : "--");
export const formatDateTime = (d) => (d ? format(new Date(d), "d MMM yyyy, HH:mm") : "--");
export const formatTime = (d) => (d ? format(new Date(d), "HH:mm") : "--");

// The browser/OS's own local timezone abbreviation -- date-fns's own
// format() above (and every plain new Date().toLocaleString() elsewhere in
// this app) already renders in this same local timezone by default, but
// never labels it. Used specifically for that: a short "IST"/"PST"/"GMT-4"
// tag next to an already-locally-formatted time, not for re-computing the
// time itself. en-IN specifically, matching the exact locale the backend's
// own formatInterviewTime (email.service.js) uses, so the abbreviation
// shown here and the one printed in an interview invite email read as the
// same label for the same timezone.
export const localTzAbbr = () =>
  new Intl.DateTimeFormat("en-IN", { timeZoneName: "short" })
    .formatToParts(new Date())
    .find((p) => p.type === "timeZoneName")?.value || "";

export const fromNow = (d) => (d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : "--");

export function relativeDay(d) {
  if (!d) return "--";
  // Same UTC-calendar-date fix as formatDate above -- a birthday or
  // holiday date is a calendar date, not an instant, so isToday/
  // isTomorrow (which read the browser's LOCAL calendar day) need the
  // same re-basing or "Today" can silently read as "Tomorrow" or
  // yesterday depending on the viewer's offset from UTC.
  const date = toCalendarDate(d);
  if (isToday(date)) return "Today";
  if (isTomorrow(date)) return "Tomorrow";
  return format(date, "d MMM");
}

// The API sends minor units. Rendering `amount / 100` inline in a component is
// how a salary ends up displayed 100x too large exactly once, in production.
// Like formatMoney, but keeps paise when there are any (e.g. ESIC of
// Rs 93.75) -- for salary breakups, where rounding would misstate pay.
export function formatMoneyExact(minorUnits, currency = "INR", locale = "en-IN") {
  if (minorUnits === null || minorUnits === undefined) return "--";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);
}

export function formatMoney(minorUnits, currency = "INR", locale = "en-IN") {
  if (minorUnits === null || minorUnits === undefined) return "--";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(minorUnits / 100);
}

export const formatNumber = (n, locale = "en-IN") =>
  n === null || n === undefined ? "--" : new Intl.NumberFormat(locale).format(n);

export const formatPercent = (n, digits = 1) =>
  n === null || n === undefined ? "--" : `${Number(n).toFixed(digits)}%`;

export const tenure = (from) => {
  if (!from) return "--";
  const years = differenceInYears(new Date(), new Date(from));
  if (years < 1) return "Under a year";
  return `${years} year${years === 1 ? "" : "s"}`;
};

export const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

// Minutes -> "1h 2m" (or just "45m" under an hour, "3h" with no leftover
// minutes) -- every place a duration is computed in minutes (late arrival,
// worked time) currently either showed the raw number ("333m late", which
// nobody reads as "5.5 hours" at a glance) or nothing at all. Treats 0 the
// same as null/undefined here -- workedMinutes defaults to 0 on a record
// with no checkout yet (see attendance.model.js), so "0m" would read as
// "worked zero minutes" rather than "nothing to show yet", which is the
// more honest reading for a still-open day.
export function formatMinutes(mins) {
  if (mins === null || mins === undefined || mins === 0) return "--";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// Acronyms that must stay in capitals after humanizing ("HRA", not "Hra").
const HUMANIZE_ACRONYMS = new Set(["HRA", "PF", "EPF", "ESI", "ESIC", "TDS", "CTC", "LOP", "PT", "LWF", "UAN", "PAN", "NOC", "HR", "ID", "LTA", "NPS", "ECR"]);

// Sentence case, not SCREAMING_SNAKE, anywhere a human reads it.
export const humanize = (value = "") =>
  String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\b[a-z]+\b/gi, (w) => (HUMANIZE_ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w));

// Converts a plain <input type="datetime-local"> value ("2026-08-12T10:00",
// with no timezone attached at all) into the correct UTC instant it
// actually represents in a given IANA timezone -- an org's own configured
// one, not whatever the browser's own local timezone happens to be. That
// distinction is the entire reason this function exists: a datetime-local
// input has no concept of timezone whatsoever, and new Date(value) parses
// it as the BROWSER's local time -- correct only by coincidence, whenever
// the person typing happens to be physically sitting in the same timezone
// as the org itself. Reliably wrong the moment they aren't (a browser/OS
// clock left on UTC, someone scheduling from a different country), which
// is exactly the bug this replaces: typing "10:00 AM" meaning 10:00 AM in
// Mumbai and having it silently scheduled for 10:00 AM UTC instead, hours
// away from what was actually intended.
//
// The approach: read the wall-clock numbers as if they already were UTC,
// find out what that same instant would display as when read back in the
// TARGET timezone instead, and the difference between those two readings
// is exactly the correction needed. Correctly accounts for DST on its
// own, since it goes through Intl's own real timezone database rather
// than a fixed offset number.
export function zonedTimeToUtc(dateTimeLocalString, timeZone) {
  if (!dateTimeLocalString) return null;
  const asIfUtc = new Date(`${dateTimeLocalString}Z`);
  const inTargetTzString = asIfUtc.toLocaleString("en-US", { timeZone, hour12: false });
  const inTargetTz = new Date(`${inTargetTzString.replace(",", "")} UTC`);
  const offset = asIfUtc.getTime() - inTargetTz.getTime();
  return new Date(asIfUtc.getTime() + offset);
}

// The exact inverse of zonedTimeToUtc above -- given a real instant (a
// Date, or anything new Date() accepts), returns the "YYYY-MM-DDTHH:mm"
// wall-clock string that same instant displays as in a given IANA
// timezone, ready to drop straight into a datetime-local input's own
// value. Needed anywhere an existing scheduledAt (always stored as a
// real UTC instant) gets shown back in a form for editing -- without
// this, that display step falls back to the browser's own local getters
// (getHours(), etc.), silently showing the wrong wall-clock time the
// moment the browser's own local timezone differs from the org's
// configured one, the exact same bug in the opposite direction.
export function utcToZonedTimeLocal(date, timeZone) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  // hour12: false can still yield "24" for midnight on some engines --
  // normalized to "00" so the datetime-local input never rejects it.
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
}
